/**
 * Product card behaviour.
 *
 * <quick-add>: adds a variant to the cart via the AJAX Cart API.
 *   - Single-variant products: the trigger submits directly.
 *   - Multi-variant products: the trigger opens a <dialog> with the options
 *     (non-modal popover over the photo on >=750px, modal bottom sheet below).
 *   Button states: aria-busy (spinner) -> data-state="added" (check) or
 *   data-state="error". On success dispatches `cart:updated` (fresh cart) and
 *   `cart:item-added` ({ item, cart, source }) for the cart toast / drawer.
 *   Size-only products render one <quick-add> per value (inline pills).
 */
(() => {
  const sheetQuery = window.matchMedia('(max-width: 749px)');

  if (!customElements.get('quick-add')) {
    customElements.define(
      'quick-add',
      class QuickAdd extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('[data-quick-add-form]');
          this.submit = this.querySelector('[data-quick-add-submit]');
          this.toggle = this.querySelector('[data-quick-add-toggle]');
          this.dialog = this.querySelector('dialog');
          if (!this.form || !this.submit) return;

          this.card = this.closest('.product-card');
          this.label = this.submit.querySelector('[data-label]');
          this.price = this.querySelector('[data-price]');
          this.status = this.querySelector('[data-status]');
          this.idInput = this.form.querySelector('input[name="id"]');
          this.defaultLabel = this.label ? this.label.textContent : '';

          const json = this.querySelector('[data-variants]');
          this.variants = json ? JSON.parse(json.textContent) : null;

          this.onDocumentClick = this.onDocumentClick.bind(this);

          if (this.toggle && this.dialog) {
            this.toggle.addEventListener('click', () => (this.dialog.open ? this.close() : this.open()));
            this.querySelectorAll('[data-quick-add-close]').forEach((button) =>
              button.addEventListener('click', () => this.close())
            );
            this.dialog.addEventListener('keydown', (event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                this.close();
              }
            });
            // Backdrop click on the modal sheet.
            this.dialog.addEventListener('click', (event) => {
              if (event.target === this.dialog) this.close();
            });
            this.dialog.addEventListener('close', () => this.afterClose());
            this.updateVariant();
          }

          this.form.addEventListener('change', () => this.updateVariant());
          this.form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.add();
          });
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocumentClick);
        }

        open() {
          if (sheetQuery.matches) this.dialog.showModal();
          else {
            this.dialog.show();
            document.addEventListener('click', this.onDocumentClick);
          }
          this.toggle.setAttribute('aria-expanded', 'true');
          if (this.card) this.card.classList.add('is-quick-open');
          const first =
            this.dialog.querySelector('input[type="radio"]:checked') || this.dialog.querySelector('input[type="radio"]');
          if (first) first.focus({ preventScroll: true });
        }

        close() {
          if (this.dialog && this.dialog.open) this.dialog.close();
        }

        afterClose() {
          document.removeEventListener('click', this.onDocumentClick);
          this.toggle.setAttribute('aria-expanded', 'false');
          if (this.card) this.card.classList.remove('is-quick-open');
          if (this.contains(document.activeElement) || document.activeElement === document.body) {
            this.toggle.focus({ preventScroll: true });
          }
        }

        onDocumentClick(event) {
          if (!this.contains(event.target)) this.close();
        }

        selectedOptions() {
          return Array.from(this.form.querySelectorAll('fieldset')).map((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          });
        }

        updateVariant() {
          if (!this.variants) return;
          const selected = this.selectedOptions();
          const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
          this.variant = variant;

          // Selected value next to each legend + strike unavailable combinations.
          this.form.querySelectorAll('fieldset').forEach((fieldset, index) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            const display = fieldset.querySelector('[data-selected-value]');
            if (display) display.textContent = checked ? checked.value : '';
            fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
              const possible = this.variants.some(
                (v) => v.available && v.options[index] === input.value && v.options.every((value, i) => i === index || value === selected[i])
              );
              input.closest('label').classList.toggle('pill--unavailable', !possible);
            });
          });

          if (variant) {
            this.idInput.value = variant.id;
            if (this.price && typeof variant.price === 'string' && variant.price) this.price.textContent = variant.price;
          }
          const available = Boolean(variant && variant.available);
          this.submit.disabled = !available;
          if (this.label) {
            this.label.textContent = available
              ? this.defaultLabel
              : variant
                ? this.dataset.textSoldOut
                : this.dataset.textUnavailable;
          }
        }

        async add() {
          if (this.submit.getAttribute('aria-busy') === 'true') return;
          clearTimeout(this.resetTimer);
          delete this.submit.dataset.state;
          this.submit.setAttribute('aria-busy', 'true');
          this.setStatus('');

          try {
            // Shared cart API (theme.js): add.js + drawer section + fresh cart.js.
            const { item, cart, sections } = await window.theme.cartAdd({
              items: [{ id: Number(this.idInput.value), quantity: 1 }],
            });

            this.submit.removeAttribute('aria-busy');
            this.submit.dataset.state = 'added';
            if (this.label) this.label.textContent = this.dataset.textAdded;
            this.setStatus(this.dataset.textAdded);

            window.theme.announceAdd({ item, cart, sections, source: this.toggle || this.submit });

            if (this.dialog && this.dialog.open) setTimeout(() => this.close(), 900);
            this.resetTimer = setTimeout(() => this.reset(), 2200);
          } catch (error) {
            this.submit.removeAttribute('aria-busy');
            this.submit.dataset.state = 'error';
            this.setStatus(error.message || this.dataset.textError);
            this.resetTimer = setTimeout(() => this.reset(), 2600);
          }
        }

        reset() {
          delete this.submit.dataset.state;
          if (this.label) this.label.textContent = this.defaultLabel;
          if (this.variants) this.updateVariant();
        }

        setStatus(message) {
          if (this.status) this.status.textContent = message;
        }

        selectValue(position, value) {
          const fieldset = this.form.querySelectorAll('fieldset')[position - 1];
          if (!fieldset) return;
          const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((el) => el.value === value);
          if (input && !input.checked) {
            input.checked = true;
            this.updateVariant();
          }
        }
      }
    );
  }
})();
